/**
 * Hungarian translations.
 *
 * Typed against the English reference catalogue: every key is checked at
 * compile time, and anything left out falls back to English at runtime.
 */

import { Translation } from './en'

const hu: Translation = {
    common: {
        language: {
            select: 'Nyelv választása'
        },
        menu: {
            collapse: 'Menü elrejtése',
            compounds: 'Hatóanyagok',
            targets: 'Gyógyszercélpontok',
            alterations: 'Kimutatott alterációk'
        },
        footer: {
            ifu: 'Használati útmutató',
            productLabel: 'Termékcímke',
            disclaimer: 'Jogi nyilatkozat',
            calculationDate: 'Számítás dátuma: {{date}}'
        },
        metaInfo: {
            tumorset: 'Tumortípus',
            fileName: 'Fájlnév',
            tumorsetMissing: 'A tumortípus adatai hiányoznak ebből a fájlból.'
        },
        a11y: {
            appLogo: 'Alkalmazás logója'
        },
        labels: {
            na: 'N/A',
            reloadPage: 'Oldal újratöltése',
            unnamedCompound: 'Névtelen hatóanyag',
            unnamedDriver: 'Névtelen driver',
            unnamedTarget: 'Névtelen célpont'
        },
        tableRowLabels: {
            DRIVER: 'Driver',
            BIOMARKER: 'Biomarker',
            NON_CONFIRMED_DRIVER: 'Ellentmondásos driver',
            VUS: 'Ismeretlen jelentőségű variáns',
            NON_DRIVER: 'Nem driver',
            VUS_IN_DRIVER: 'VUS driver génben',
            GENOMIC_MARKER: 'Genomikai marker'
        },
        testTypeAndTestResult: {
            NGS_NORMAL: 'vad típusú gén',
            NGS_MUTANT: 'mutáns gén',
            NGS_UNKNOWN: 'ismeretlen státuszú gén',
            AMPLIFICATION_MUTANT: 'amplifikáció jelen van',
            AMPLIFICATION_NORMAL: 'amplifikáció nincs jelen',
            TRANSLOCATION_MUTANT: 'transzlokáció jelen van',
            TRANSLOCATION_NORMAL: 'transzlokáció nincs jelen',
            LOSS_MUTANT: 'génvesztés jelen van',
            LOSS_NORMAL: 'génvesztés nincs jelen',
            IHC_NORMAL: 'normál',
            IHC_OVER: 'overexpresszió',
            IHC_BELOW: 'expresszió hiánya',
            MSI_HIGH: 'MSI-H',
            MSI_LOW: 'MSI-L',
            MSI_MSS: 'MSS',
            TMB_HIGH: 'TMB-H',
            TMB_LOW: 'TMB-L',
            HRD_POSITIVE: 'HRD-pozitív',
            HRD_NEGATIVE: 'HRD-negatív',
            TIL_HIGH: 'TIL-H',
            TIL_LOW: 'TIL-L',
            IHC_LOW: 'alacsony expresszió',
            UNKNOWN: ''
        },
        buttons: {
            goBackHome: 'Vissza a főoldalra'
        },
        errors: {
            404: '404 – Nem található',
            notFound: {
                description: 'A keresett oldal nem található.'
            },
            unknownError: 'Ismeretlen hiba',
            unexpected: 'Váratlan hiba történt.'
        },
        contact: {
            mailTo: '<1>support@genomate.health</1>',
            haveQuestions: 'Ha bármilyen kérdése van',
            contactUsAnyTime:
                'kérjük vegye fel velünk a kapcsolatot a következő címen:'
        },
        fileUploader: {
            disabledMessage: 'A feltöltés le van tiltva',
            dropHereMessage: 'Húzza ide',
            defaultDescription: 'Húzza ide a fájlt, vagy tallózzon',
            fileTypeErrorMessage: 'A fájltípus nem támogatott',
            fileSizeErrorMessage: 'A fájl mérete túl nagy',
            fileSizeTooSmallErrorMessage: 'A fájl mérete túl kicsi',
            invalidJson: 'Érvénytelen JSON fájl',
            readError: 'Hiba a fájl olvasása során',
            size: 'méret',
            types: 'típusok'
        }
    },
    compound: {
        title: 'Hatóanyagok',
        tableHeader: {
            compound: 'Hatóanyag',
            drivers: 'Driverek',
            targets: 'Gyógyszercélpontok',
            gds: 'GDS'
        },
        noCompoundsFound: 'Ehhez a pácienshez nem találtunk hatóanyagot.'
    },
    molecularBiomarkers: {
        listTitle: 'Kimutatott alterációk'
    },
    targets: {
        listTitle: 'Gyógyszercélpontok',
        tableHeader: {
            target: 'Gyógyszercélpont',
            relatedDrivers: 'Kapcsolódó driverek',
            gts: 'GTS'
        },
        noDruggableTargetsFound:
            'Ehhez a pácienshez nem találtunk gyógyszerrel támadható célpontot.'
    },
    pages: {
        disclaimer: {
            title: 'Jogi nyilatkozat'
        },
        ifu: {
            title: 'Használati útmutató'
        },
        productLabel: {
            title: 'Termékcímke'
        },
        upload: {
            title: 'JSON feltöltése'
        }
    },
    evidence: {
        COMPOUND: {
            type: 'hatóanyag'
        },
        TARGET: {
            type: 'gyógyszercélpont'
        },
        DRIVER: {
            type: 'alteráció'
        },
        evidenceList: 'Evidencialista',
        showAllRelations: 'Összes reláció',
        noEvidenceAvailable: 'Nincs elérhető evidencia',
        labels: {
            relationType: 'reláció típusa',
            researchType: 'Kutatás típusa',
            tumor: 'tumor',
            histology: 'hisztológia',
            driver: 'driver',
            biomarker: 'biomarker',
            target: 'gyógyszercélpont',
            molecularBackground: 'molekuláris háttér',
            compound: 'hatóanyag',
            score: 'pontszám'
        }
    },
    filter: {
        filter: {
            CLINICAL: 'Klinikai evidencia',
            PRECLINICAL: 'Preklinikai evidencia',
            IN_SILICO: 'In-silico evidencia',
            MATCH_TUMOR: 'A páciens tumortípusa',
            MATCH_PROFILE: 'A páciens pontos alterációi',
            addFilter: 'Szűrő hozzáadása',
            addFilterEvidence: 'Szűrő',
            THIS_TUMOR_TYPE: 'Ez a tumortípus',
            OTHER_TUMOR_TYPE: 'Más tumortípusok',
            DEVELOPMENT: 'Fejlesztés alatt',
            clearAll: 'Összes törlése',
            inputPlaceholder: 'Hatóanyag keresése név szerint',
            or: 'VAGY',
            negative: 'Negatív GDS',
            positive: 'Pozitív GDS'
        },
        filterTooltip: {
            CLINICAL:
                'Klinikai vizsgálatokból és emberi résztvevőkkel végzett tanulmányokból származó evidenciák megjelenítése',
            PRECLINICAL:
                'Laboratóriumi vizsgálatokból és állatmodellekből származó evidenciák megjelenítése',
            IN_SILICO:
                'Számítási modellekből vagy bioinformatikai előrejelzésekből származó evidenciák megjelenítése',
            MATCH_TUMOR:
                'A páciens diagnosztizált tumortípusára jellemző evidenciák megjelenítése',
            MATCH_PROFILE:
                'Csak a páciens konkrét molekuláris eltéréseihez kapcsolódó evidenciák megjelenítése (pl. KRAS G12D), a szélesebb, génszintű evidenciák kizárásával (pl. KRAS-mutáns).'
        }
    },
    report: {
        biomarkersAndInhibitors: {
            tableHeader: {
                variant: 'Alteráció',
                driverClassification: 'Klasszifikáció',
                gbs: 'GBS',
                chevron: ''
            },
            noDriversAndBiomarkersFound:
                'Ehhez a pácienshez nem találtunk drivereket és biomarkereket.'
        }
    }
}

export default hu
